import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import mermaid from 'mermaid';
import { FiMaximize2, FiX, FiZoomIn, FiZoomOut, FiDownload, FiCode, FiSun, FiMoon } from 'react-icons/fi';

// ── Mermaid DOM cleanup ─────────────────────────────────────────
// Removes orphaned error/SVG elements that mermaid.render() leaks into the DOM.
export const cleanupMermaidErrors = () => {
  const errorSelectors = [
    '[id*="mermaid-error"]',
    '.mermaid-error',
    '.error-text',
    '.error-icon',
    '[id^="d3-"]',
    '.marker',
    '[id^="dmermaid-"]',
    'svg[id^="mermaid-"][role="graphics-document document"][aria-roledescription="error"]',
  ];

  document.querySelectorAll(errorSelectors.join(', ')).forEach(el => {
    try { el.remove(); } catch {}
  });

  document.querySelectorAll('div, pre, span, text').forEach(el => {
    if (
      el.textContent &&
      (el.textContent.includes('syntax error in text, mermaid') ||
        el.textContent.includes('Parse error') ||
        el.textContent.includes('Lexical error'))
    ) {
      try {
        if (!el.closest('.message-bubble') || el.classList.contains('error-text')) {
          el.remove();
        }
      } catch {}
    }
  });

  document
    .querySelectorAll(
      'body > svg[id^="mermaid-"], body > svg[id^="dmermaid-"], body > div[id^="dmermaid-"], body > svg[aria-roledescription="error"]'
    )
    .forEach(el => {
      try { el.remove(); } catch {}
    });
};

// ── Sanitize & theme mermaid code ───────────────────────────────
export const sanitizeMermaidCode = (code: string, darkMode: boolean): string => {
  code = code.replace(/```mermaid\n|\n```/g, '');

  // LLM-mistake coercion: single-% comments → %%. Mermaid's only comment
  // syntax is %%; LLMs sometimes emit % (SQL/LaTeX style) which makes the
  // parser throw on the first comment line and the whole diagram falls
  // back to its raw source. Match `^<whitespace>%` not followed by
  // another % so existing %% comments are untouched. Caught
  // 2026-05-16 on the terraform-migration flowchart that recurred 6×
  // across turns in the same session.
  code = code.replace(/^(\s*)%(?!%)/gm, '$1%%');

  // JSON-escape leak: some envelope paths leave \" and \] in mermaid
  // source where unescaped " and ] are required. Defensive unescape —
  // backslash-quote and backslash-bracket are not valid mermaid syntax
  // for any diagram type, so coercing is safe.
  code = code.replace(/\\"/g, '"');
  code = code.replace(/\\\]/g, ']');

  // Doubled closing quote before `]`: when an LLM over-escapes inside a
  // quoted label it writes patterns like `pulumi-ts/\""]` intending one
  // closing quote. After the \" unescape above we end up with `""]`,
  // which mermaid rejects (Expecting 'SQE'...got 'STR'). Collapse `""`
  // to `"` ONLY when followed by `]` and NOT immediately preceded by
  // `[` — the latter preserves legitimate empty labels `[""]`.
  code = code.replace(/(?<!\[)""(?=\])/g, '"');

  // Paren-wrapped cylinder: LLMs sometimes wrap mermaid's cylinder
  // shape `id[(text)]` with extra parens, producing `id([(text)])`
  // which mermaid rejects (Expecting 'TAGEND','STR'...got 'PS'). Strip
  // the redundant outer parens. Caught 2026-05-16 on three terraform-
  // migration variants ("Modules to Migrate" cylinder).
  code = code.replace(/\(\[\(([\s\S]*?)\)\]\)/g, '[($1)]');

  // xychart-beta: inject config block (uses different config path than theme)
  if (code.trim().startsWith('xychart')) {
    const xyConfig = darkMode
      ? `---\nconfig:\n  themeVariables:\n    xyChart:\n      backgroundColor: "#1f2937"\n      titleColor: "#60A5FA"\n      xAxisLabelColor: "#9ca3af"\n      xAxisTitleColor: "#d1d5db"\n      xAxisTickColor: "#4b5563"\n      xAxisLineColor: "#4b5563"\n      yAxisLabelColor: "#9ca3af"\n      yAxisTitleColor: "#d1d5db"\n      yAxisTickColor: "#4b5563"\n      yAxisLineColor: "#4b5563"\n      plotColorPalette: "#3B82F6,#10b981,#8b5cf6,#f59e0b,#ef4444,#14b8a6"\n---\n`
      : `---\nconfig:\n  themeVariables:\n    xyChart:\n      backgroundColor: "#f9fafb"\n      titleColor: "#111827"\n      plotColorPalette: "#3B82F6,#10b981,#8b5cf6,#f59e0b,#ef4444,#14b8a6"\n---\n`;
    if (!code.includes('config:')) {
      code = xyConfig + code;
    }
    return code;
  }

  // Fix pie chart entries crammed on same line
  code = code
    .split('\n')
    .map(line => {
      const matches = line.match(/"[^"]+"\s*:\s*\d+/g);
      if (matches && matches.length > 1) {
        return matches.map(m => '    ' + m.trim()).join('\n');
      }
      return line;
    })
    .join('\n');

  // Fix common LLM Gantt syntax issues
  code = code.replace(/:crit,\s*active,\s*/g, ':crit, ');
  code = code.replace(/(:\w+,)\s*[a-zA-Z][\w-]*,\s*/g, '$1 ');
  code = code.replace(/:([ ]*)[a-zA-Z][\w]*,\s*(?=after|2\d{3})/g, ':$1');
  code = code.replace(/:crit,\s*[a-zA-Z][\w-]*,\s*/g, ':crit, ');
  code = code.replace(/after\s+[a-zA-Z][\w]*,?\s*/g, '');
  code = code.replace(/(\d{4}-\d{2}-\d{2}),\s*(\d{4}-\d{2}-\d{2})/g, (_, start, end) => {
    const s = new Date(start);
    const e = new Date(end);
    const days = Math.max(1, Math.round((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24)));
    return `${start}, ${days}d`;
  });

  // Inject brand-colored theme if no theme directive already exists
  if (!code.includes('%%{init:') && !code.includes('%%{init :')) {
    const themeVars = darkMode
      ? {
          primaryColor: '#3B82F6',
          primaryTextColor: '#ffffff',
          primaryBorderColor: '#60A5FA',
          secondaryColor: '#8b5cf6',
          secondaryTextColor: '#ffffff',
          secondaryBorderColor: '#a78bfa',
          tertiaryColor: '#10b981',
          tertiaryTextColor: '#ffffff',
          tertiaryBorderColor: '#34d399',
          lineColor: '#60A5FA',
          textColor: '#e5e7eb',
          mainBkg: '#1f2937',
          nodeBorder: '#60A5FA',
          clusterBkg: '#111827',
          titleColor: '#60A5FA',
          edgeLabelBackground: '#1f2937',
          pie1: '#3B82F6',
          pie2: '#10b981',
          pie3: '#8b5cf6',
          pie4: '#f59e0b',
          pie5: '#ef4444',
          pie6: '#14b8a6',
          pie7: '#ec4899',
          pie8: '#6366f1',
          pieStrokeColor: '#374151',
          pieTitleTextColor: '#e5e7eb',
          pieSectionTextColor: '#ffffff',
          pieLegendTextColor: '#d1d5db',
          pieStrokeWidth: '1px',
          pieOuterStrokeColor: '#374151',
          gridColor: '#374151',
          todayLineColor: '#f59e0b',
          doneTaskBkgColor: '#10b981',
          doneTaskBorderColor: '#059669',
          activeTaskBkgColor: '#3B82F6',
          activeTaskBorderColor: '#2563eb',
          critBkgColor: '#ef4444',
          critBorderColor: '#dc2626',
          taskBkgColor: '#4b5563',
          taskBorderColor: '#6b7280',
          taskTextColor: '#ffffff',
          sectionBkgColor: '#111827',
          sectionBkgColor2: '#1f2937',
          altSectionBkgColor: '#111827',
        }
      : {
          primaryColor: '#3B82F6',
          primaryTextColor: '#ffffff',
          secondaryColor: '#8b5cf6',
          secondaryTextColor: '#ffffff',
          tertiaryColor: '#10b981',
          tertiaryTextColor: '#ffffff',
          lineColor: '#3B82F6',
          textColor: '#374151',
          pie1: '#3B82F6',
          pie2: '#10b981',
          pie3: '#8b5cf6',
          pie4: '#f59e0b',
          pie5: '#ef4444',
          pie6: '#14b8a6',
          pie7: '#ec4899',
          pie8: '#6366f1',
          pieTitleTextColor: '#111827',
          todayLineColor: '#f59e0b',
          doneTaskBkgColor: '#10b981',
          activeTaskBkgColor: '#3B82F6',
          taskBkgColor: '#d1d5db',
        };

    const themeStr = JSON.stringify(themeVars);
    code = `%%{init: {'theme': 'base', 'themeVariables': ${themeStr}}}%%\n${code}`;
  }

  return code;
};

// ── MermaidBlock component ──────────────────────────────────────
// Extracted from CodeBlock to fix Rules of Hooks violation (hooks were
// called inside a conditional branch). This is now a proper top-level component.
//
// NOTE: dangerouslySetInnerHTML is used intentionally here to render SVG output
// from mermaid.render(). The content is safe because:
// 1. mermaid.render() generates SVG from a DSL, not from arbitrary HTML
// 2. securityLevel is set to 'strict' which encodes tags and disables scripts
// 3. The input is sanitized through sanitizeMermaidCode() before rendering

interface MermaidBlockProps {
  code: string;
  darkMode: boolean;
  children: React.ReactNode;
}

const MermaidBlock: React.FC<MermaidBlockProps> = ({ code, darkMode, children }) => {
  const [rendered, setRendered] = useState<string | null>(null);
  const mermaidContainerRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);

  // Zoom modal state
  const [showZoom, setShowZoom] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [zoomThemeLight, setZoomThemeLight] = useState(false);
  const [showSource, setShowSource] = useState(false);

  // Pan state
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  // isPanning tracked via ref only — no state needed (cursor set imperatively)
  const panStartRef = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0 });
  const diagramAreaRef = useRef<HTMLDivElement>(null);

  // ── Render mermaid diagram ──────────────────────────────────
  useEffect(() => {
    if (!mermaidContainerRef.current) return;
    let cancelled = false;

    const doRender = async () => {
      try {
        const sanitizedCode = sanitizeMermaidCode(code, darkMode);
        const id = `mmd-${Math.random().toString(36).substring(2, 9)}`;

        mermaid.initialize({
          startOnLoad: true,
          theme: darkMode ? 'dark' : 'default',
          securityLevel: 'strict',
        });

        const { svg } = await mermaid.render(id, sanitizedCode);
        if (!cancelled) {
          setRendered(svg);
        }
      } catch (err: any) {
        if (!cancelled) {
          console.warn('Mermaid rendering failed:', err.message);
          setRendered(null);
        }
      }
      cleanupMermaidErrors();
    };

    const timer = setTimeout(doRender, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      cleanupMermaidErrors();
    };
  }, [code, darkMode]);

  // ── Cleanup on unmount ──────────────────────────────────────
  useEffect(() => () => { cleanupMermaidErrors(); }, []);

  // ── Escape key closes modal ─────────────────────────────────
  useEffect(() => {
    if (!showZoom) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowZoom(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showZoom]);

  // ── Reset pan when modal opens ──────────────────────────────
  useEffect(() => {
    if (showZoom) setPanOffset({ x: 0, y: 0 });
  }, [showZoom]);

  // ── Mouse wheel zoom + click-drag pan in modal ─────────────
  // Refs mirror state for the closure — avoids re-registering listeners every frame
  const isPanningRef = useRef(false);
  const panOffsetRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (!showZoom || !diagramAreaRef.current) return;
    const el = diagramAreaRef.current;

    const wheelHandler = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setZoomLevel(z => Math.min(5, Math.max(0.1, z + delta)));
    };
    el.addEventListener('wheel', wheelHandler, { passive: false });

    const mouseDownHandler = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest('pre, button')) return;
      isPanningRef.current = true;
      panStartRef.current = { x: e.clientX, y: e.clientY, offsetX: panOffsetRef.current.x, offsetY: panOffsetRef.current.y };
      el.style.cursor = 'grabbing';
    };
    const mouseMoveHandler = (e: MouseEvent) => {
      if (!isPanningRef.current) return;
      const dx = e.clientX - panStartRef.current.x;
      const dy = e.clientY - panStartRef.current.y;
      const newOffset = { x: panStartRef.current.offsetX + dx, y: panStartRef.current.offsetY + dy };
      panOffsetRef.current = newOffset;
      setPanOffset(newOffset);
    };
    const mouseUpHandler = () => {
      isPanningRef.current = false;
      el.style.cursor = '';
    };

    el.addEventListener('mousedown', mouseDownHandler);
    window.addEventListener('mousemove', mouseMoveHandler);
    window.addEventListener('mouseup', mouseUpHandler);

    return () => {
      el.removeEventListener('wheel', wheelHandler);
      el.removeEventListener('mousedown', mouseDownHandler);
      window.removeEventListener('mousemove', mouseMoveHandler);
      window.removeEventListener('mouseup', mouseUpHandler);
    };
  }, [showZoom]);

  // ── Copy to clipboard ───────────────────────────────────────
  const copyToClipboard = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // ── Download SVG/PNG ────────────────────────────────────────
  const handleDownload = (format: 'svg' | 'png') => {
    if (!rendered) return;

    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(rendered, 'image/svg+xml');
    const svgEl = svgDoc.querySelector('svg');
    if (!svgEl) return;

    const viewBox = svgEl.getAttribute('viewBox');
    let svgWidth = 1200;
    let svgHeight = 800;
    if (viewBox) {
      const parts = viewBox.split(/[\s,]+/).map(Number);
      if (parts.length === 4) {
        svgWidth = parts[2];
        svgHeight = parts[3];
      }
    }
    const attrW = parseFloat(svgEl.getAttribute('width') || '0');
    const attrH = parseFloat(svgEl.getAttribute('height') || '0');
    if (attrW > 0 && attrH > 0) {
      svgWidth = attrW;
      svgHeight = attrH;
    }

    const exportSvg = svgEl.cloneNode(true) as SVGElement;
    exportSvg.setAttribute('width', String(svgWidth));
    exportSvg.setAttribute('height', String(svgHeight));
    exportSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    const exportStr = new XMLSerializer().serializeToString(exportSvg);

    if (format === 'svg') {
      const blob = new Blob([exportStr], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'diagram.svg';
      a.click();
      URL.revokeObjectURL(url);
    } else {
      const scale = 2;
      const canvas = document.createElement('canvas');
      canvas.width = svgWidth * scale;
      canvas.height = svgHeight * scale;
      const ctx = canvas.getContext('2d')!;

      ctx.fillStyle = zoomThemeLight ? '#ffffff' : '#111827';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const svgBlob = new Blob([exportStr], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, svgWidth * scale, svgHeight * scale);
        canvas.toBlob(blob => {
          if (!blob) return;
          const pngUrl = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = pngUrl;
          a.download = 'diagram.png';
          a.click();
          URL.revokeObjectURL(pngUrl);
        }, 'image/png');
        URL.revokeObjectURL(url);
      };
      img.src = url;
    }
  };

  // NOTE: dangerouslySetInnerHTML usage is safe here — see component JSDoc above
  return (
    <>
      {/* Fullscreen modal */}
      {showZoom &&
        rendered &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 bg-black/80 z-[9999] flex flex-col"
            onMouseDown={e => {
              if (e.target === e.currentTarget) setShowZoom(false);
            }}
          >
            {/* Toolbar */}
            <div className="flex items-center justify-between px-4 py-3 bg-gray-900 border-b border-gray-700 shrink-0">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setZoomLevel(z => Math.max(0.25, z - 0.25))}
                  className="p-1.5 rounded hover:bg-gray-700 text-gray-300"
                  title="Zoom out"
                >
                  <FiZoomOut className="w-4 h-4" />
                </button>
                <span className="text-xs text-gray-400 w-12 text-center">{Math.round(zoomLevel * 100)}%</span>
                <button
                  onClick={() => setZoomLevel(z => Math.min(4, z + 0.25))}
                  className="p-1.5 rounded hover:bg-gray-700 text-gray-300"
                  title="Zoom in"
                >
                  <FiZoomIn className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    setZoomLevel(1);
                    setPanOffset({ x: 0, y: 0 });
                  }}
                  className="px-2 py-1 rounded text-xs hover:bg-gray-700 text-gray-400"
                >
                  Fit
                </button>
                <div className="w-px h-5 bg-gray-700 mx-1" />
                <button
                  onClick={() => setZoomThemeLight(!zoomThemeLight)}
                  className="p-1.5 rounded hover:bg-gray-700 text-gray-300"
                  title="Toggle background"
                >
                  {zoomThemeLight ? <FiMoon className="w-4 h-4" /> : <FiSun className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => setShowSource(!showSource)}
                  className={`p-1.5 rounded hover:bg-gray-700 ${showSource ? 'text-violet-400 bg-gray-700' : 'text-gray-300'}`}
                  title="View source"
                >
                  <FiCode className="w-4 h-4" />
                </button>
                <div className="w-px h-5 bg-gray-700 mx-1" />
                <button
                  onClick={() => handleDownload('svg')}
                  className="px-2 py-1 rounded text-xs hover:bg-gray-700 text-gray-400 flex items-center gap-1"
                >
                  <FiDownload className="w-3 h-3" /> SVG
                </button>
                <button
                  onClick={() => handleDownload('png')}
                  className="px-2 py-1 rounded text-xs hover:bg-gray-700 text-gray-400 flex items-center gap-1"
                >
                  <FiDownload className="w-3 h-3" /> PNG
                </button>
              </div>
              <button
                onClick={() => setShowZoom(false)}
                className="p-2 rounded hover:bg-red-900/50 text-gray-300 hover:text-white"
                title="Close (Esc)"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
            {/* Diagram area */}
            <div
              ref={diagramAreaRef}
              className="flex-1 overflow-hidden flex items-center justify-center"
              onMouseDown={e => {
                if (e.target === e.currentTarget) {
                  const startX = e.clientX,
                    startY = e.clientY;
                  const handler = (up: MouseEvent) => {
                    const dist = Math.abs(up.clientX - startX) + Math.abs(up.clientY - startY);
                    if (dist < 5) setShowZoom(false);
                    window.removeEventListener('mouseup', handler);
                  };
                  window.addEventListener('mouseup', handler);
                }
              }}
            >
              {showSource ? (
                <pre className="p-6 rounded-lg bg-gray-800 text-gray-300 text-sm whitespace-pre-wrap max-w-5xl w-full m-8 overflow-auto">
                  {code}
                </pre>
              ) : (
                <div
                  className={`p-8 rounded-xl transition-colors ${zoomThemeLight ? 'bg-white' : 'bg-gray-900'}`}
                  style={{
                    transform: `scale(${zoomLevel}) translate(${panOffset.x / zoomLevel}px, ${panOffset.y / zoomLevel}px)`,
                    transformOrigin: 'center center',
                    cursor: zoomLevel > 1 ? 'grab' : 'default',
                  }}
                  ref={el => {
                    if (el) {
                      const svg = el.querySelector('svg');
                      if (svg) {
                        svg.style.width = '80vw';
                        svg.style.maxWidth = '1200px';
                        svg.style.height = 'auto';
                      }
                    }
                  }}
                  dangerouslySetInnerHTML={{ __html: rendered }}
                />
              )}
            </div>
          </div>,
          document.body
        )}

      {/* Inline diagram */}
      <div
        ref={mermaidContainerRef}
        className="relative group"
        style={{ minHeight: '50px', maxHeight: '600px' }}
      >
        {/* Action buttons */}
        <div className="absolute right-2 top-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
          {rendered && (
            <button
              onClick={() => setShowZoom(true)}
              className="p-1.5 rounded bg-gray-700/90 text-gray-300 hover:bg-gray-600 backdrop-blur-sm"
              aria-label="Fullscreen"
              title="View fullscreen"
            >
              <FiMaximize2 className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={copyToClipboard}
            className="p-1.5 rounded bg-gray-700/90 text-gray-300 hover:bg-gray-600 backdrop-blur-sm"
            aria-label="Copy code"
            title="Copy diagram source"
          >
            {copied ? (
              <span className="text-green-400 text-xs">&#10003;</span>
            ) : (
              <span className="text-xs">&#128203;</span>
            )}
          </button>
        </div>

        {!rendered ? (
          <pre
            className={`p-3 rounded-lg text-sm overflow-auto ${darkMode ? 'bg-gray-800 text-gray-300 border border-gray-700' : 'bg-gray-50 text-gray-700 border border-gray-200'}`}
          >
            <code>{children}</code>
          </pre>
        ) : (
          <div
            onClick={() => setShowZoom(true)}
            className={`mermaid-container p-3 rounded-lg my-2 cursor-pointer hover:ring-2 hover:ring-violet-500/30 transition-shadow ${darkMode ? 'bg-gray-800/80 border border-gray-700' : 'bg-gray-50 border border-gray-200'} overflow-auto`}
            style={{ maxHeight: '600px' }}
            title="Click to expand"
            dangerouslySetInnerHTML={{ __html: rendered }}
          />
        )}
      </div>
    </>
  );
};

export default MermaidBlock;
